import React, { useEffect } from "react";
import { Modal, Form, Input, Select, Checkbox } from "antd";
import { useFetchApi } from "../../hooks/useFetchApi";
import { captialCase } from "../../helper";

const { Option } = Select;

const metric_types = [
  { name: "Balance Sheet", value: "balance_sheet" },
  { name: "Income Statement", value: "income_statement" },
];

const AddNewModal: React.FC<{
  status: boolean;
  onClose: any;
  onSuccess?: any;
  data?: any;
  metrics?: any[];
}> = ({
  status,
  onClose,
  onSuccess,
  data = {},
  metrics = [],
}): React.ReactElement => {
  const [form] = Form.useForm();

  const { execute, loading } = useFetchApi(
    "/api/metrics",
    {},
    data && Object.keys(data).length ? "PUT" : "POST",
    onSuccess,
  );

  const { execute: fetchMetricCategories, response: metricCategories } =
    useFetchApi("/api/metrics/categories", {}, "GET");

  const isEdit = data && Object.keys(data).length > 0;

  const handleOnSubmit = () => {
    form.validateFields().then((values) => {
      execute({
        name: values.name,
        metric_type: values.section,
        metric_category_id: values.category,
        parent_metric_id: values.parentMetric ?? null,
        create_table: values.create_table ? 1 : 0,
        is_visible: values.is_visible ? 1 : 0,
        id: data.id ? data.id : undefined,
      });
      form.setFieldsValue({
        name: undefined,
        section: metric_types[0].value,
        category: 1,
        parentMetric: null,
        create_table: true,
        is_visible: true,
      });
      onClose({ target: { id: isEdit ? "edit" : "new" } });
    });
  };

  const handleClose = () => {
    form.setFieldsValue({
      name: undefined,
      section: metric_types[0].value,
      category: 1,
      parentMetric: null,
      create_table: true,
      is_visible: false,
    });
    onClose({ target: { id: isEdit ? "edit" : "new" } });
  };

  const nameValue = Form.useWatch("name", form);

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        name: data.name,
        section: data.from,
        category: data.metric_category_id,
        parentMetric: data.parent_metric_id || null,
        create_table: data.create_table === 1,
        is_visible: data.is_visible === 1,
      });
    } else {
      form.setFieldsValue({
        name: undefined,
        section: metric_types[0].value,
        category: 1,
        parentMetric: null,
        create_table: true,
        is_visible: false,
      });
    }
  }, [data]);

  useEffect(() => {
    fetchMetricCategories();
  }, []);

  return (
    <Modal
      title="Add New Metrics"
      open={status}
      onOk={handleOnSubmit}
      onCancel={handleClose}
      okText={isEdit ? "Update" : "Add"}
      cancelText="Cancel"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{ disabled: !nameValue?.trim() }}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          section: metric_types[0].value,
          category: 1,
          subCategory: null,
          create_table: true,
          is_visible: false,
        }}
      >
        <Form.Item
          label="Metric Name"
          name="name"
          rules={[{ required: true, message: "Please enter a metric name" }]}
        >
          <Input placeholder="Enter metric name" />
        </Form.Item>

        <Form.Item label="Metric Type" name="section">
          <Select>
            {metric_types.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {metricCategories && metricCategories.length > 0 && (
          <Form.Item label="Metric Category" name="category">
            <Select>
              {metricCategories.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item label="Parent Metric" name="parentMetric">
          <Select
            allowClear
            showSearch
            placeholder="Select parent metric (optional)"
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            options={metrics
              .filter((m) => m.id !== data?.id)
              .map((m) => ({
                label: captialCase(m.name),
                value: m.id,
              }))}
          />
        </Form.Item>

        <div className="flex gap-6">
          <Form.Item name="create_table" valuePropName="checked">
            <Checkbox disabled={isEdit}>Create Table</Checkbox>
          </Form.Item>

          <Form.Item name="is_visible" valuePropName="checked">
            <Checkbox>Is Visible</Checkbox>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default React.memo(AddNewModal);
